import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { contentAPI } from '../services/contentApi';
import { mediaUrl } from '../services/mediaApi';

const FALLBACK = {
  page_images: {},
  pricing: {
    flipper_rates: [],
    leisure_games: [],
    sport_machines: [],
    padel_note: '',
    fitness_monthly: '35€/mois',
  },
  pinball_machines: [],
  partners: {
    title: 'Nos partenaires',
    subtitle: '',
    open_slots: 4,
    devis_label: 'Partenariat',
    items: [],
  },
  club_settings: {},
};

const resolveContentUrls = (data) => {
  if (!data) return FALLBACK;
  const page_images = Object.fromEntries(
    Object.entries(data.page_images || {}).map(([k, v]) => [k, mediaUrl(v)])
  );
  const pinball_machines = (data.pinball_machines || []).map((m) => ({
    ...m,
    image: mediaUrl(m.image),
  }));
  const partnersRaw = data.partners || FALLBACK.partners;
  const partners = {
    ...FALLBACK.partners,
    ...partnersRaw,
    open_slots: partnersRaw.open_slots ?? 4,
    items: (partnersRaw.items || []).map((p) => {
      const logo = p.logo || '';
      const isPublicAsset =
        logo.startsWith('/partners/')
        || logo.startsWith('/ja-padel')
        || logo.startsWith('/chantilly');
      return {
        ...p,
        logo: isPublicAsset ? logo : mediaUrl(logo),
      };
    }),
  };
  return { ...FALLBACK, ...data, page_images, pinball_machines, partners };
};

const CACHE_KEY = 'chantilly_site_content_v5';

export const clearSiteContentCache = () => {
  try {
    sessionStorage.removeItem(CACHE_KEY);
    sessionStorage.removeItem('chantilly_site_content_v4');
    sessionStorage.removeItem('chantilly_site_content_v3');
    sessionStorage.removeItem('chantilly_site_content_v2');
    sessionStorage.removeItem('chantilly_site_content_v1');
  } catch {
    /* ignore */
  }
};
const CACHE_TTL_MS = 30 * 1000;

const readCache = () => {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw);
    if (Date.now() - ts > CACHE_TTL_MS) return null;
    return data;
  } catch {
    return null;
  }
};

const writeCache = (data) => {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({ data, ts: Date.now() }));
  } catch {
    /* quota / private mode */
  }
};

const SiteContentContext = createContext({
  content: FALLBACK,
  loading: true,
  reload: async () => {},
});

export const SiteContentProvider = ({ children }) => {
  const [content, setContent] = useState(() => resolveContentUrls(readCache()) || FALLBACK);
  const [loading, setLoading] = useState(!readCache());

  const reload = useCallback(async (force = false) => {
    const cached = !force ? readCache() : null;

    if (cached) {
      setContent(resolveContentUrls(cached));
      setLoading(false);
      // Rafraîchissement en arrière-plan sans bloquer l'affichage
      try {
        const data = await contentAPI.getPublic();
        const merged = resolveContentUrls({ ...FALLBACK, ...data });
        setContent(merged);
        writeCache(merged);
      } catch {
        /* garde le cache */
      }
      return;
    }

    setLoading(true);
    try {
      const data = await contentAPI.getPublic();
      const merged = resolveContentUrls({ ...FALLBACK, ...data });
      setContent(merged);
      writeCache(merged);
    } catch (error) {
      console.error('Contenu site:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload(true);
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      reload(true);
    };
    const onAdminUpdate = () => reload(true);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('chantilly-site-content-updated', onAdminUpdate);
    window.addEventListener('focus', onAdminUpdate);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('chantilly-site-content-updated', onAdminUpdate);
      window.removeEventListener('focus', onAdminUpdate);
    };
  }, [reload]);

  const value = useMemo(() => ({ content, loading, reload }), [content, loading, reload]);

  return (
    <SiteContentContext.Provider value={value}>
      {children}
    </SiteContentContext.Provider>
  );
};

export const useSiteContent = () => useContext(SiteContentContext);

export default useSiteContent;
